import {
  getMissingContractorProfileFields,
  type ContractorProfileData,
} from './contractorProfile';

/**
 * A quick estimate produces one of two documents, and which one it is decides
 * what may be printed on it.
 *
 * `test`   — the draft a contractor pulls before they have a profile, and the
 *            only document a logged-out visitor can get. It carries the whole
 *            estimate (items, VAT, areas) but never contractor identity: an
 *            incomplete or placeholder company name on a page a client might see
 *            is worse than no name at all.
 * `client`  — the real offer. It requires a complete, validated profile, and a
 *            wrong or sample NIP still blocks it.
 */
export type OfferDocumentVariant = 'test' | 'client';

export const TEST_OFFER_TITLE = 'ROBOCZY PDF TESTOWY';
export const TEST_OFFER_CONTRACTOR_LINE = 'Dane wykonawcy: nie podano';
export const TEST_OFFER_DISCLAIMER = 'Dokument nie stanowi oferty handlowej.';

/** True when the profile is good enough to be printed as a real offer. */
export function isContractorProfileComplete(
  profile: ContractorProfileData | null | undefined
): boolean {
  return getMissingContractorProfileFields(profile).length === 0;
}

export interface OfferDocumentVariantInput {
  isLoggedIn: boolean;
  contractor?: ContractorProfileData | null;
}

/**
 * Logged out is always a test document — a visitor has no profile to stand
 * behind an offer. Logged in earns the client variant only once the profile is
 * complete and valid.
 */
export function resolveOfferDocumentVariant(
  input: OfferDocumentVariantInput
): OfferDocumentVariant {
  if (!input.isLoggedIn) return 'test';
  return isContractorProfileComplete(input.contractor) ? 'client' : 'test';
}

/** The non-blocking hint shown when the estimate falls back to a test document. */
export function getTestVariantNotice(isLoggedIn: boolean): string {
  return isLoggedIn
    ? 'To roboczy PDF testowy. Uzupełnij dane firmy, żeby przygotować właściwą ofertę dla klienta.'
    : 'To roboczy PDF testowy. Dane firmy dodasz po zalogowaniu, gdy będziesz chciał wysłać ofertę klientowi.';
}
